import { useEffect, useState } from 'react'
import { Pagination, Payload, Result, SearchSpringProductResponse } from '../../search-spring/types/types'
import InstantSearchGrid from '../../search-spring/components/InstantSearchGrid'
import { convertSearchSpringProductPricingToMarketPrice, isSearchSpringProductResponse, standardSearch } from '../../helpers/searchSpringHelpers'
import PaginationBar from './PaginationBar'
import CollectionSearchGrid from '../../search-spring-collection/components/CollectionSearchGrid'
import useWindowSize from '../../search-spring/customHooks/useWindowSize'

type CollectionDisrupterData = {
  height?: string
  width?: string
  tile_position?: number
  tile_position_mobile?: number
}

type Props = {
  pagination: Pagination
  products: Result[]
  searchQuery: string
  payload: Payload,
  isSearchPage?: boolean
}

declare var Shopify: { currency: { active: string } }
const COLLECTION_GRID_BUFFER = 2

const updatePageUrl = (pageNumber: number | null) => {
  if (!pageNumber) return
  const url = new URL(window.location.href)
  url.searchParams.set('page', pageNumber.toString())
  window.history.pushState({}, '', url)
}

const PaginationService = ({pagination, products, searchQuery, payload, isSearchPage = true}: Props) => {
  const [searchPageProducts, setSearchPageProducts] = useState<Result[]>(products)
  const [nextPage, setNextPage] = useState<number | null>(pagination.nextPage)
  const [currentProductCount, setCurrentProductCount] = useState<number>(pagination.end)
  const [error, setError] = useState(false)
  const [carryoverProducts, setCarryoverProducts] = useState<Result[]>([])
  const isCollectionSearch = isSearchPage == false ? true : false
  const windowSize = useWindowSize()
  const baseResultsPerPage = windowSize.width <= 640 ? CD_STATE.searchspring.numberOfProductsMobile : CD_STATE.searchspring.numberOfProductsDesktop
  const isMobile = windowSize.width <= 640
  // TODO: handle push state, pop state here

  const getCollectionPageCarryover = (pageNumber: number) => {
    if (!isCollectionSearch || isMobile) return 0

    const disrupterData: CollectionDisrupterData[] = [
      CD_STATE?.collectionDisrupter?.collectionDisrupterData1,
      CD_STATE?.collectionDisrupter?.collectionDisrupterData2,
      CD_STATE?.collectionDisrupter?.collectionDisrupterData3,
    ]

    const disruptorsAffectingLoadedPages = disrupterData.filter((data) => {
      const height = (data?.height ?? 'screen').toLowerCase()
      const width = (data?.width ?? '50%').toLowerCase()
      const tilePosition = data?.tile_position

      return height === 'card' && width === '50%' && typeof tilePosition === 'number' && tilePosition < pageNumber * baseResultsPerPage
    })

    return disruptorsAffectingLoadedPages.length % 2 === 1 ? COLLECTION_GRID_BUFFER : 0
  }

  const splitCollectionPageProducts = (pageProducts: Result[], pageNumber: number, nextPageNumber: number | null, existingCarryoverProducts: Result[] = []) => {
    const mergedProducts = [...existingCarryoverProducts, ...pageProducts]
    const nextCarryoverCount = nextPageNumber ? getCollectionPageCarryover(pageNumber) : 0
    const visibleProducts = mergedProducts.length - nextCarryoverCount

    return {
      pageProducts: mergedProducts.slice(0, visibleProducts),
      carryoverProducts: mergedProducts.slice(visibleProducts),
    }
  }

  const getInitialCollectionProducts = () => {
    const currentPage = pagination.currentPage ?? 1

    if (!isCollectionSearch) {
      return {
        visibleProducts: products,
        carryoverProducts: [] as Result[],
      }
    }

    let visibleProducts: Result[] = []
    let nextCarryoverProducts: Result[] = []

    for (let pageNumber = 1; pageNumber <= currentPage; pageNumber++) {
      const pageStart = (pageNumber - 1) * baseResultsPerPage
      const pageEnd = pageStart + baseResultsPerPage
      const currentPageProducts = products.slice(pageStart, pageEnd)
      const nextPageNumber = pageNumber < currentPage ? pageNumber + 1 : pagination.nextPage
      const splitProducts = splitCollectionPageProducts(currentPageProducts, pageNumber, nextPageNumber, nextCarryoverProducts)

      visibleProducts = [...visibleProducts, ...splitProducts.pageProducts]
      nextCarryoverProducts = splitProducts.carryoverProducts
    }

    return {
      visibleProducts,
      carryoverProducts: nextCarryoverProducts,
    }
  }

  // Load products on page taking into account disrupter grid carryover
  useEffect(() => {
    const initialCollectionProducts = getInitialCollectionProducts()

    setSearchPageProducts(initialCollectionProducts.visibleProducts)
    setCarryoverProducts(initialCollectionProducts.carryoverProducts)
    setCurrentProductCount(initialCollectionProducts.visibleProducts.length)
  }, [products, pagination.currentPage, pagination.nextPage, isCollectionSearch, baseResultsPerPage, isMobile])

  const loadMoreProducts = async () => {
    const updatedPayload: Payload = {
      ...payload
    }
    if (isCollectionSearch) {
      updatedPayload.resultsPerPage = baseResultsPerPage
    }
    if (nextPage) {
      updatedPayload.page = nextPage
    }
    const pageNumber = updatedPayload.page ?? 1

    try {
      const response: SearchSpringProductResponse | unknown = await standardSearch({searchQuery: searchQuery, payload: updatedPayload, searchType: isCollectionSearch ? 'collection' : 'search' })          
      if (isSearchSpringProductResponse(response)) {
        let newProducts = response.results
        if (Shopify.currency.active != "AUD" || response.results.some(product => Number(product.price) === 0)) {
          const newproductData: SearchSpringProductResponse = await convertSearchSpringProductPricingToMarketPrice(response, Shopify.currency.active === "AUD")
          // Incase the product conversion fails just put in the AU price, the PDP and cart will show correct prices.
          newProducts = newproductData.results ? newproductData.results : response.results
        }

        if (isCollectionSearch && pageNumber > 1) {
          const splitProducts = splitCollectionPageProducts(newProducts, pageNumber, response.pagination.nextPage, carryoverProducts)
          const pageProducts = splitProducts.pageProducts
          const nextCarryover = splitProducts.carryoverProducts

          if (nextCarryover.length === 0) {
            setCarryoverProducts([])
            setSearchPageProducts(prev => prev = [...prev, ...pageProducts, ...nextCarryover])
            setCurrentProductCount(prev => prev + pageProducts.length + nextCarryover.length)
          } else {
            setCarryoverProducts(nextCarryover)
            setSearchPageProducts(prev => prev = [...prev, ...pageProducts])
            setCurrentProductCount(prev => prev + pageProducts.length)
          }
        } else {
          setCarryoverProducts([])
          setSearchPageProducts(prev => prev = [...prev, ...newProducts])
          setCurrentProductCount(response.pagination.end)
        }

        setNextPage(response.pagination.nextPage ?? null)
        
        requestAnimationFrame(() => {
          // Updating page param.
          updatePageUrl(updatedPayload.page || null)
          window.scrollTo(0, scrollY)
        })
      } else {
        setError(true)
        throw new Error 
      } 
    } catch (error) {
      setError(true)
    }
  }

  const ComponentHandler = (): JSX.Element => {
    if (error) {
      return <div className="text-xl font-bstSpyre mt-4 text-center mx-5">Sorry, there was an error getting more products. Please try again later or contact us if this issue persists.</div>
    }
    return <>
      {searchPageProducts.length > 0 ? (
        isCollectionSearch ? (
          <CollectionSearchGrid products={searchPageProducts} />
        ) : (
          <InstantSearchGrid products={searchPageProducts} />
        )
      ) : (
        <div className="text-xl font-bstSpyre my-4 text-center mx-5">
          If you are not seeing any results, try removing some of your selected filters.
        </div>
      )}
      
      {nextPage ? (
        <div className='flex flex-col items-center my-LG md:my-DLG'>
          <div className='font-calibre text-sm-b uppercase text-black'>{`${currentProductCount} of ${pagination.totalResults}`}</div>
          
          <div className='mb-SM md:mb-DSM'>
          <PaginationBar currentProductCount={currentProductCount} totalResults={pagination.totalResults} maxWidth={210} tailwindClass='mt-2'/>
          </div>

          <button onClick={loadMoreProducts} className="Button Button--secondary-dark uppercase backdrop-filter-none min-w-[150px] h-[35px] md:h-[44px] font-calibre text-sm-b text-black hover:text-white focus:text-[var(--ink)]">Load More</button>
        </div>
      ) : null}

      {(!nextPage && CD_STATE.nextCollection) ? (
        <div className='flex justify-center my-LG md:my-DLG'>
          <a href={CD_STATE.nextCollection.url} className="Button Button--secondary-dark block min-w-[150px] h-[35px] md:h-[44px] font-calibre text-sm-b text-black hover:text-white focus:text-[var(--ink)] uppercase backdrop-filter-none">
            {CD_STATE.nextCollection.title}
          </a>
        </div>
      ) : null}
    </>
  }

  return (
    <ComponentHandler />
  )
}

export default PaginationService
